export type CollaborationRole = "student" | "trainer";

export interface CollaborationEnvelope {
    aad: string;
    alg: "A256GCM";
    ciphertext: string;
    nonce: string;
    schemaVersion: 1;
}

export interface CollaborationSecrets {
    capability: string;
    key: string;
}

export interface CollaborationProjectPayload {
    name?: string;
    project: ArrayBuffer;
}

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder("utf-8", { fatal: true });
const projectPayloadMagic = new Uint8Array([0x53, 0x43, 0x47, 0x31]);

export const encodeCollaborationProject = (
    project: ArrayBuffer,
    name: string,
): ArrayBuffer => {
    const normalizedName = name.trim();
    if (
        !normalizedName ||
        Array.from(normalizedName).length > 100 ||
        project.byteLength === 0
    )
        throw new Error("INVALID_COLLABORATION_PROJECT");
    const encodedName = textEncoder.encode(normalizedName);
    const output = new Uint8Array(
        projectPayloadMagic.length + 4 + encodedName.length + project.byteLength,
    );
    output.set(projectPayloadMagic, 0);
    new DataView(output.buffer).setUint32(
        projectPayloadMagic.length,
        encodedName.length,
    );
    output.set(encodedName, projectPayloadMagic.length + 4);
    output.set(
        new Uint8Array(project),
        projectPayloadMagic.length + 4 + encodedName.length,
    );
    return output.buffer;
};

export const decodeCollaborationProject = (
    input: ArrayBuffer,
): CollaborationProjectPayload => {
    const bytes = new Uint8Array(input);
    const isContainer = projectPayloadMagic.every(
        (value, index) => bytes[index] === value,
    );
    if (!isContainer) return { project: input };
    if (bytes.length < projectPayloadMagic.length + 5)
        throw new Error("INVALID_COLLABORATION_PROJECT");
    const nameLength = new DataView(input).getUint32(projectPayloadMagic.length);
    const contentOffset = projectPayloadMagic.length + 4 + nameLength;
    if (nameLength === 0 || contentOffset >= bytes.length)
        throw new Error("INVALID_COLLABORATION_PROJECT");
    const name = textDecoder
        .decode(bytes.subarray(projectPayloadMagic.length + 4, contentOffset))
        .trim();
    if (!name || Array.from(name).length > 100)
        throw new Error("INVALID_COLLABORATION_PROJECT");
    return {
        name,
        project: bytes.slice(contentOffset).buffer,
    };
};

export const encodeBase64Url = (data: Uint8Array): string => {
    let binary = "";
    for (let offset = 0; offset < data.length; offset += 0x8000) {
        binary += String.fromCharCode(
            ...data.subarray(offset, offset + 0x8000),
        );
    }
    return btoa(binary)
        .replaceAll("+", "-")
        .replaceAll("/", "_")
        .replace(/=+$/u, "");
};

export const decodeBase64Url = (value: string): Uint8Array<ArrayBuffer> => {
    if (!/^[A-Za-z0-9_-]+$/u.test(value)) throw new Error("INVALID_BASE64URL");
    const padding = "=".repeat((4 - (value.length % 4)) % 4);
    const binary = atob(
        value.replaceAll("-", "+").replaceAll("_", "/") + padding,
    );
    const decoded = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1)
        decoded[index] = binary.charCodeAt(index);
    if (encodeBase64Url(decoded) !== value)
        throw new Error("INVALID_BASE64URL");
    return decoded;
};

export const randomSecret = (): string =>
    encodeBase64Url(crypto.getRandomValues(new Uint8Array(32)));

export const canonicalCollaborationAad = (
    sessionId: string,
    revision: number,
    parentRevision: number | null,
    authorRole: CollaborationRole,
): string =>
    JSON.stringify({ sessionId, revision, parentRevision, authorRole });

const importKey = async (encodedKey: string): Promise<CryptoKey> => {
    const key = decodeBase64Url(encodedKey);
    if (key.length !== 32) throw new Error("INVALID_COLLABORATION_KEY");
    return crypto.subtle.importKey("raw", key, { name: "AES-GCM" }, false, [
        "encrypt",
        "decrypt",
    ]);
};

export const encryptProject = async (
    project: ArrayBuffer,
    encodedKey: string,
    sessionId: string,
    revision: number,
    parentRevision: number | null,
    authorRole: CollaborationRole,
): Promise<CollaborationEnvelope> => {
    const key = await importKey(encodedKey);
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const aadText = canonicalCollaborationAad(
        sessionId,
        revision,
        parentRevision,
        authorRole,
    );
    const aad = textEncoder.encode(aadText);
    const ciphertext = new Uint8Array(
        await crypto.subtle.encrypt(
            { name: "AES-GCM", iv: nonce, additionalData: aad, tagLength: 128 },
            key,
            project,
        ),
    );
    return {
        schemaVersion: 1,
        alg: "A256GCM",
        nonce: encodeBase64Url(nonce),
        ciphertext: encodeBase64Url(ciphertext),
        aad: encodeBase64Url(aad),
    };
};

export const decryptProject = async (
    envelope: CollaborationEnvelope,
    encodedKey: string,
    sessionId: string,
    revision: number,
    parentRevision: number | null,
    authorRole: CollaborationRole,
): Promise<ArrayBuffer> => {
    const nonce = decodeBase64Url(envelope.nonce);
    if (nonce.length !== 12) throw new Error("INVALID_COLLABORATION_NONCE");
    const aad = decodeBase64Url(envelope.aad);
    const expectedAad = canonicalCollaborationAad(
        sessionId,
        revision,
        parentRevision,
        authorRole,
    );
    if (textDecoder.decode(aad) !== expectedAad)
        throw new Error("INVALID_COLLABORATION_AAD");
    const key = await importKey(encodedKey);
    return crypto.subtle.decrypt(
        { name: "AES-GCM", iv: nonce, additionalData: aad, tagLength: 128 },
        key,
        decodeBase64Url(envelope.ciphertext),
    );
};

export const encryptProjectPayload = async (
    payload: { name: string; project: ArrayBuffer },
    encodedKey: string,
    sessionId: string,
    revision: number,
    parentRevision: number | null,
    authorRole: CollaborationRole,
): Promise<CollaborationEnvelope> =>
    encryptProject(
        encodeCollaborationProject(payload.project, payload.name),
        encodedKey,
        sessionId,
        revision,
        parentRevision,
        authorRole,
    );

export const decryptProjectPayload = async (
    envelope: CollaborationEnvelope,
    encodedKey: string,
    sessionId: string,
    revision: number,
    parentRevision: number | null,
    authorRole: CollaborationRole,
): Promise<CollaborationProjectPayload> =>
    decodeCollaborationProject(
        await decryptProject(
            envelope,
            encodedKey,
            sessionId,
            revision,
            parentRevision,
            authorRole,
        ),
    );

export const trainerLink = (
    origin: string,
    sessionId: string,
    key: string,
    trainerCapability: string,
): string => `${origin}/s/${sessionId}#v=1&k=${key}&c=${trainerCapability}`;

export const trainerShareLink = (
    origin: string,
    shareUrl: string | undefined,
    sessionId: string,
    key: string,
    trainerCapability: string,
): string =>
    shareUrl
        ? new URL(shareUrl, origin).toString()
        : trainerLink(origin, sessionId, key, trainerCapability);

export const parseTrainerFragment = (
    fragment: string,
): CollaborationSecrets => {
    const parameters = new URLSearchParams(
        fragment.startsWith("#") ? fragment.slice(1) : fragment,
    );
    const key = parameters.get("k") ?? "";
    const capability = parameters.get("c") ?? "";
    if (
        parameters.get("v") !== "1" ||
        decodeBase64Url(key).length !== 32 ||
        decodeBase64Url(capability).length !== 32
    )
        throw new Error("INVALID_TRAINER_LINK");
    return { key, capability };
};
